import { Separator } from "../components/ui/separator";
import { SidebarNav } from "../components/account/components/sidebar-nav";
import Header from "../components/header/header";
import { ProfileForm } from "../components/account/components/profile-form";
import { TwoFactorAuthForm } from "../components/account/components/two-factor-authentication";
import { ChangePassword } from "../components/account/components/change-password";
import { UserPreferencesForm } from "../components/account/components/preferences";
import { ApiKeyManagement } from "../components/account/components/api-keys";
import MyTeam from "../components/account/components/team";
import Reports from "../components/account/components/reports";
import { Billing } from "../components/account/components/billing";
import AuditLog from "../components/account/components/audit-log";
import AgentPayoutHistory from "../components/account/components/agent-payout-history";
import { isProduction } from "../lib/utils";
import { useAuth } from "../lib/AuthContext";
import config from "../config";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useLocation } from "react-router-dom";
const stripePromise = loadStripe(config.STRIPE_KEY);

// TODO: options object appears unused - commented out for cleanup
// const options = {
//   // passing the client secret obtained from the server
//   // clientSecret: "{{CLIENT_SECRET}}",
//   // mode: "setup",
//   // currency: "usd",
//   // Fully customizable with appearance API.
//   // appearance: {
//   //   /*...*/
//   // },
// };

export default function Account() {
  const location = useLocation();
  const currentPath = location.pathname;

  const auth = useAuth();
  const flags = auth?.featureFlags;
  const permissions = auth.currentUser?.user_permissions[0] || {};
  permissions.can_manage_billing = true;

  // Debug logging to understand feature flag issue
  console.log("Feature Flags:", flags);
  console.log("Current User:", auth?.currentUser);

  const isAllowed = (flag: string) => flags.includes(flag);

  const sidebarNavItems = [
    {
      title: "Profile",
      href: "/account/",
    },
    {
      title: "Change Password",
      href: "/account/password/",
    },
  ];

  // Always show 2FA tab for now (fallback logic)
  const show2FA = isAllowed("2fa") || auth?.currentUser?.totp_enabled || true;
  console.log("Is 2FA Allowed:", isAllowed("2fa"));
  console.log("Show 2FA:", show2FA);

  show2FA &&
    sidebarNavItems.push({
      title: "Two Factor Authentication",
      href: "/account/two-factor-authentication/",
    });

  if (permissions.can_edit_preferences) {
    sidebarNavItems.push({
      title: "Preferences",
      href: "/account/preferences/",
    });

    isAllowed("api_keys") &&
      sidebarNavItems.push({
        title: "API Keys",
        href: "/account/api-keys/",
      });
  }

  if (permissions.can_manage_users) {
    isAllowed("team") &&
      sidebarNavItems.push({
        title: "Team",
        href: "/account/team/",
      });
  }

  if (permissions.can_view_reports) {
    isAllowed("reports") &&
      sidebarNavItems.push({
        title: "Reports",
        href: "/account/reports/",
      });
  }

  if (permissions.can_manage_billing) {
    sidebarNavItems.push({
      title: "Billing",
      href: "/account/billing/",
    });
  }

  isAllowed("audit_log") &&
    sidebarNavItems.push({
      title: "Audit Log",
      href: "/account/audit-log/",
    });

  // Add Payout History for agents
  sidebarNavItems.push({
    title: "Payout History",
    href: "/account/payout-history/",
  });

  // Function to render the appropriate component based on the current path
  const renderContent = () => {
    if (currentPath === "/account/" || currentPath === "/account") {
      return <ProfileForm />;
    } else if (currentPath === "/account/password/") {
      return <ChangePassword />;
    } else if (currentPath === "/account/preferences/") {
      return <UserPreferencesForm />;
    } else if (currentPath === "/account/api-keys/" && isAllowed("api_keys")) {
      return <ApiKeyManagement />;
    } else if (currentPath === "/account/team/" && isAllowed("team")) {
      return <MyTeam />;
    } else if (currentPath === "/account/reports/" && isAllowed("reports")) {
      return <Reports />;
    } else if (currentPath === "/account/billing/") {
      return (
        <Elements stripe={stripePromise}>
          <Billing />
        </Elements>
      );
    } else if (
      currentPath === "/account/two-factor-authentication/" &&
      isAllowed("2fa")
    ) {
      return <TwoFactorAuthForm />;
    } else if (
      currentPath === "/account/audit-log/" &&
      isAllowed("audit_log")
    ) {
      return <AuditLog />;
    } else if (currentPath === "/account/payout-history/") {
      return <AgentPayoutHistory />;
    } else {
      return <ProfileForm />;
    }
  };

  return (
    <>
      <Header />
      <div className="hidden space-y-6 p-10 pb-16 md:block">
        <div className="space-y-0.5 mt-12">
          <h2 className="text-2xl font-bold tracking-tight">My Account</h2>
          <p className="text-muted-foreground">
            Manage your account settings and set e-mail preferences.
          </p>
        </div>
        <Separator className="my-6" />
        <div className="flex flex-col space-y-8 lg:flex-row lg:space-x-12 lg:space-y-0">
          <aside className="-mx-4 lg:w-1/5">
            <SidebarNav items={sidebarNavItems} />
          </aside>
          <div className="flex-1 lg:max-w-3xl">{renderContent()}</div>
        </div>
      </div>
    </>
  );
}
